import { useState, useEffect, useMemo } from "react";
import { DealerLayout } from "@/components/shared/DealerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dealerSubmitLocation, getPageContent, uploadFile } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Loader2, Send, CheckCircle, ImagePlus, X, Upload } from "lucide-react";

const usStates = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"
];

const DEFAULT_FIELDS = [
  { name: "store_name", label: "Store Name", type: "text", required: true, placeholder: "e.g. Prime Wireless - Downtown" },
  { name: "address", label: "Street Address", type: "text", required: true, placeholder: "123 Main Street" },
  { name: "city", label: "City", type: "text", required: true },
  { name: "state", label: "State", type: "select", required: true },
  { name: "zip_code", label: "ZIP Code", type: "text", required: true },
  { name: "phone", label: "Phone Number", type: "tel", required: false, placeholder: "(555) 123-4567" },
  { name: "hours", label: "Store Hours", type: "text", required: false, placeholder: "Mon-Fri: 9AM-7PM, Sat: 10AM-5PM" },
  { name: "description", label: "Additional Details", type: "textarea", required: false, placeholder: "Any additional information about the location" },
];

function getSection(sections, name, fallback = {}) {
  const found = sections.find((s) => s.section_name === name && s.active !== false);
  if (!found) return fallback;
  return { ...fallback, ...found, metadata: { ...(fallback.metadata || {}), ...(found.metadata || {}) } };
}

export default function DealerSubmitLocation() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({});
  const [sections, setSections] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { getPageContent("submit-location").then(setSections).catch(() => {}); }, []);

  const formSection = getSection(sections, "form", { title: "Submit a New Store Location", content: "Fill out the details below and our team will review your submission." });

  const formFields = useMemo(() => {
    const cmsFields = formSection.metadata?.fields;
    return (Array.isArray(cmsFields) && cmsFields.length > 0) ? cmsFields : DEFAULT_FIELDS;
  }, [formSection]);

  useEffect(() => {
    const initial = {};
    formFields.forEach((f) => { initial[f.name] = ""; });
    setFormData(initial);
  }, [formFields]);

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (images.length + files.length > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const result = await uploadFile(file, "location-images");
        setImages((prev) => [...prev, { url: result.url, name: file.name }]);
      }
      toast.success(`${files.length} image(s) uploaded`);
    } catch (err) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const requiredFields = formFields.filter((f) => f.required);
    const missing = requiredFields.filter((f) => !formData[f.name]);
    if (missing.length > 0) return toast.error("Please fill in all required fields");

    setLoading(true);
    try {
      await dealerSubmitLocation({ ...formData, images: images.map((img) => img.url) });
      toast.success("Location submitted for review!");
      setSubmitted(true);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to submit location");
    } finally { setLoading(false); }
  };

  const handleReset = () => {
    const reset = {};
    formFields.forEach((f) => { reset[f.name] = ""; });
    setFormData(reset);
    setImages([]);
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <DealerLayout title="Submit New Location">
        <div className="max-w-xl mx-auto py-12 text-center" data-testid="location-submitted">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-navy mb-3">Location Submitted!</h2>
          <p className="text-slate-500 mb-6 font-body">Your new location request has been submitted to the admin team for review. You'll be notified once it's been processed.</p>
          <Button onClick={handleReset} className="bg-teal hover:bg-teal/90" data-testid="submit-another-btn">Submit Another Location</Button>
        </div>
      </DealerLayout>
    );
  }

  const regularFields = formFields.filter((f) => f.type !== "textarea");
  const textareaFields = formFields.filter((f) => f.type === "textarea");

  return (
    <DealerLayout title="Submit New Location">
      <div className="max-w-2xl mx-auto" data-testid="submit-location-page">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-teal/10 rounded-lg flex items-center justify-center">
                <MapPin className="w-6 h-6 text-teal" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl text-navy">{formSection.title}</h2>
                <p className="text-sm text-slate-500 font-body">{formSection.content}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="submit-location-form">
              {regularFields.map((field) => {
                if (field.type === "select" && field.name === "state") {
                  return (
                    <div key={field.name}>
                      <Label htmlFor={field.name}>{field.label}{field.required ? " *" : ""}</Label>
                      <Select value={formData[field.name] || ""} onValueChange={(v) => setFormData({ ...formData, [field.name]: v })}>
                        <SelectTrigger data-testid={`select-${field.name}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>{usStates.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  );
                }
                return (
                  <div key={field.name}>
                    <Label htmlFor={field.name}>{field.label}{field.required ? " *" : ""}</Label>
                    <Input
                      id={field.name}
                      name={field.name}
                      type={field.type || "text"}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      required={field.required}
                      placeholder={field.placeholder || ""}
                      data-testid={`input-${field.name}`}
                    />
                  </div>
                );
              })}

              {textareaFields.map((field) => (
                <div key={field.name}>
                  <Label htmlFor={field.name}>{field.label}{field.required ? " *" : ""}</Label>
                  <Textarea
                    id={field.name}
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={handleChange}
                    rows={4}
                    placeholder={field.placeholder || ""}
                    data-testid={`input-${field.name}`}
                  />
                </div>
              ))}

              {/* Image Upload Section */}
              <div data-testid="image-upload-section">
                <Label>Store Photos (optional, max 5)</Label>
                <div className="mt-2">
                  {images.length > 0 && (
                    <div className="flex flex-wrap gap-3 mb-3">
                      {images.map((img, idx) => (
                        <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden border border-slate-200 group">
                          <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-image-${idx}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {images.length < 5 && (
                    <div>
                      <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" id="location-images" data-testid="image-upload-input" />
                      <label htmlFor="location-images">
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center cursor-pointer hover:border-teal/50 transition-colors">
                          {uploading ? (
                            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                              <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                              <ImagePlus className="w-5 h-5" />
                              <span>Click to upload photos ({images.length}/5)</span>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full bg-teal hover:bg-teal/90 text-white font-semibold" size="lg" data-testid="submit-location-btn">
                {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</> : <><Send className="w-5 h-5 mr-2" /> Submit Location for Review</>}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </DealerLayout>
  );
}
